import type { WorkspaceSidebar } from "./sidebar";

export type Webapps = "lcm" | "dashboard" | "auth" | "lmp" | "toolbox" | "lco";

const ports: Record<Webapps, number> = {
  lcm: 3112,
  dashboard: 3110,
  auth: 3117,
  lmp: 3111,
  toolbox: 3113,
  lco: 3116,
};

export function getEntrypointService(entrypoint: string): Webapps | null {
  switch (entrypoint) {
    case "DashboardV2":
      return "dashboard";
    case "LCO":
      return "lco";
    case "LMP":
      return "lmp";
    case "LCM":
      return "lcm";
    case "Toolbox":
      return "toolbox";
    default:
      return null;
  }
}

const mappers = {
  local: (port: number) => `http://localhost:${port}`,
  dev: (webapp: Webapps) =>
    window.location.origin.replace(
      /\.(lcm|auth|lmp|lco|toolbox|dashboard)\./,
      `.${webapp}.`,
    ),
};

function getHost(service: Webapps): string {
  return window._msBaseUrl
    ? window.location.origin
    : window.location.hostname === "localhost"
      ? mappers.local(ports[service])
      : mappers.dev(service);
}

type WebappsFunction = (
  path: string,
  queryString?: Record<string, string>,
  keepParams?: (key: string, value: string) => boolean,
) => string;

type GetWorkspaceWebappsOptions = {
  sidebar: WorkspaceSidebar;
  fromService?: Webapps;
};

function createServicePath(
  service: Webapps,
  { sidebar, fromService }: GetWorkspaceWebappsOptions,
): WebappsFunction {
  return (path, queryString, keepParams): string => {
    if (path.startsWith("http://") || path.startsWith("https://")) {
      throw new Error("Absolute URLs are not supported");
    }

    if (fromService === service) {
      // TODO: need to add queryString and keepParams
      return path;
    }

    const base = window._msBaseUrl
      ? sidebar.services.find(
          (s) => getEntrypointService(s.entrypoint) === service,
        )?.url || ""
      : "";
    const url = new URL(base + path, getHost(service));

    for (const [key, value] of new URLSearchParams(window.location.search)) {
      if (keepParams?.(key, value)) {
        url.searchParams.append(key, value);
      }
    }

    if (queryString) {
      Object.entries(queryString).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
    }
    return url.toString();
  };
}

export type WorkspaceWebapps = Record<Webapps, WebappsFunction>;

export function getWorkspaceWebapps(
  options: GetWorkspaceWebappsOptions,
): WorkspaceWebapps {
  return {
    lcm: createServicePath("lcm", options),
    dashboard: createServicePath("dashboard", options),
    auth: createServicePath("auth", options),
    lmp: createServicePath("lmp", options),
    toolbox: createServicePath("toolbox", options),
    lco: createServicePath("lco", options),
  };
}
